import React, { memo, useCallback, useMemo } from 'react';
import './index.scss';
import { Calendar, CalendarEvent, CalendarView } from '@modules/common/Calendar';
import { toMoment } from '@core/utils/time';
import { TaskId, TaskResponse } from '@business/entities/task';
import { openAddEditTaskModal } from '@modules/dashboard/DashboardPage/TasksMenu/AddEditTaskModal';
import { CBody } from '@modules/common/CBody';
import { TodosPageBloc } from '@modules/TodosPage/TodosPageBloc';
import { useHistory } from '@core/router';
import { useLazyEffect } from '@core/utils/react';
import { UserAPI } from '@business/api/user';
import { useCompany } from '@core/utils/hooks/misc';
import { tap } from 'rxjs/operators';
import { useDidMount } from '@core/utils/hooks/react';
import { TaskService } from '@business/services/task_service';

interface CalendarTaskListProps {
  allTasks: TaskResponse[];
  bloc: TodosPageBloc;
  isCompleted?: boolean;
}
export const CalendarTaskList = memo((props: CalendarTaskListProps) => {
  const { allTasks, bloc, isCompleted = false } = props;
  const history = useHistory();
  const tasks = useMemo(() => allTasks.filter(o => o.dueDate && (isCompleted ? !!o.completedAt : !o.completedAt)), [allTasks, isCompleted]);
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const [view, setView] = React.useState<CalendarView>(CalendarView.month);
  const company = useCompany();
  useLazyEffect(() => {
    if (!configLoaded) return;
    UserAPI.saveMeta(`todo-calendar-${company.id}`, view).subscribe();
  }, [view]);

  useDidMount(() => {
    UserAPI.findMeta<CalendarView>(`todo-calendar-${company.id}`)
      .pipe(
        tap(data => setView(data.value || CalendarView.month)),
        tap(() => setConfigLoaded(true)),
      )
      .subscribe();
  });

  const onUpdate = useCallback(
    (id: TaskId, task: Partial<TaskResponse>) => {
      bloc.updateTask(id, task, true).subscribe();
    },
    [bloc],
  );

  // Helper function to get styling for tasks based on overdue status
  const getTaskStyling = useCallback((task: TaskResponse) => {
    // Check if task is overdue and not completed (since we're already filtering for incomplete tasks)
    const isOverdue = TaskService.isTaskOverdue(task.dueDate);

    if (isOverdue) {
      return {
        backgroundColor: '#dc3545', // Bootstrap danger red
        textColor: '#ffffff', // White text for contrast
        borderColor: '#dc3545',
        className: 'past-due-task', // Additional CSS class for custom styling
      };
    }

    return {
      backgroundColor: task.status?.color,
    };
  }, []);

  const taskEvents = useMemo(() => {
    const allTasks: CalendarEvent[] = [];
    tasks.map(o => {
      if (o.dueDate) {
        const hasTime = toMoment(o.dueDate).endOf('day').unix() !== toMoment(o.dueDate).unix();
        const styling = getTaskStyling(o);

        allTasks.push({
          id: o.id,
          title: o.title,
          start: hasTime ? toMoment(o.dueDate).unix() : toMoment(o.dueDate).endOf('day').add(-60, 'minute').unix(),
          //   end: hasTime ? toMoment(o.dueDate).endOf('day').unix() : toMoment(o.dueDate).endOf('day').add(-60, 'minute').unix(),
          item: o,
          type: 'event',
          resourceId: 'tasks',
          ...styling,
          menuOrder: o.menuOrder,
        });
      }

      o.children.map(c => {
        if (c.dueDate) {
          const hasTime = toMoment(c.dueDate).endOf('day').unix() !== toMoment(c.dueDate).unix();
          const styling = getTaskStyling(c);
          allTasks.push({
            id: c.id,
            title: c.title,
            start: hasTime ? toMoment(c.dueDate).unix() : toMoment(c.dueDate).endOf('day').add(-60, 'minute').unix(),
            item: c,
            type: 'event',
            resourceId: 'tasks',
            ...styling,
            menuOrder: c.menuOrder,
          });
        }
      });
    });
    return allTasks;
  }, [tasks, getTaskStyling]);

  const onClick = useCallback(
    (taskId: TaskId) => {
      history.push(`/todos/calendar/task/${taskId}`);
    },
    [history],
  );
  if (!configLoaded) return null;
  return (
    <CBody>
      <Calendar
        initialView={view}
        datesSet={d => setView(d.view.type as CalendarView)}
        stickyHeaderDates
        nowIndicator
        resources={[{ id: 'tasks', title: 'Tasks' }]}
        displayEventTime={false}
        onDayClick={d => openAddEditTaskModal({ dueDate: d.endOf('day').unix(), onDone: () => bloc.onReload() })}
        onEventClick={(id, item) => onClick(id)}
        onEventChange={(id, item, start, end) => onUpdate(id, { dueDate: toMoment(start).endOf('day').unix() })}
        eventOrder="menuOrder"
        types={[CalendarView.month, CalendarView.week, CalendarView.resource]}
        events={taskEvents}
      />
    </CBody>
  );
});
